// Yeshiva University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - YU has no current Common Data Set: its Office of Institutional Research
//    posts only the 2023-2024 edition (CDS_2023_2024.xlsx). The first-year
//    counts come from the U.S. Department of Education's IPEDS Admissions
//    survey for Fall 2024, first-time degree-seeking students (College
//    Navigator and the ADM2024 complete data file, unit 197708), which is
//    newer than that CDS.
//  - apply.yu.edu (YU Undergraduate Admissions): Important Deadlines (Fall
//    2027 entry), Freshman Applicants, Honors at Yeshiva University. The
//    deadlines page says YU's Early Decision is for Honors applicants; YU's
//    own news story "Students Flock to YU Early Decision" (March 27, 2025)
//    calls it "its binding Early Decision program".
//  - YU Office of International Student and Scholar Services, Estimated
//    Program Costs: the 2026-27 budget for a full-time undergraduate. It is
//    the only published 2026-27 undergraduate budget with indirect costs;
//    its tuition ($49,550) and fees ($4,950) match the Office of Student
//    Finance's 2026-2027 tuition page, and its items sum to its $84,675
//    total. It includes $6,000 in estimated health insurance.
//  - College Scorecard, school id 197708 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=197708#admsns',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'YU Undergraduate Admissions, Important Deadlines',
        url: 'https://apply.yu.edu/deadlines',
        read: '2026-10-01',
    },
    freshman: {
        label: 'YU Undergraduate Admissions, Freshman Applicants',
        url: 'https://apply.yu.edu/freshman',
        read: '2026-10-01',
    },
    honors: {
        label: 'YU Undergraduate Admissions, Honors at Yeshiva University',
        url: 'https://apply.yu.edu/honors',
        read: '2026-10-01',
    },
    edNews: {
        label: 'YU News, Students Flock to YU Early Decision (March 27, 2025)',
        url: 'https://www.yu.edu/news/yu/students-flock-yu-early-decision-choosing-world-class-education-values-driven-community',
        read: '2026-10-01',
    },
    costs: {
        label: 'YU International Student and Scholar Services, Estimated Program Costs 2026-27',
        url: 'https://international.oiss.yu.edu/index.cfm?FuseAction=Abroad.ViewLink&Parent_ID=0&Link_ID=95D18021-A5CF-0D40-F20DFF8BCC77257F',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?197708-Yeshiva-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'yeshiva-university',
    kbId: 'yeshiva_university',
    name: 'Yeshiva University',
    shortName: 'Yeshiva',
    location: 'New York, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (YU marketing branding page); decoration only.
    branding: {
        primary: '#325A89',
        secondary: '#555150',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/yeshiva_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 1665,
        admitted: 926,
        enrolled: 566,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'YU publishes no first-year acceptance rate by college or major, and no current Common Data Set; the federal IPEDS survey reports one rate for the whole university.',
        source: 'ipeds',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to YU with the YU Application or the Common App, and every applicant has an interview. The Honors programs ask for more:',
        rows: [
            { school: 'Honors programs (Yeshiva College, Stern College for Women, Sy Syms School of Business)', requirement: 'Apply through Early Decision only. You need official SAT or ACT scores sent to YU by the Honors deadline (scores you type in yourself or screenshots of score reports do not count), an essay of 650 words or less, a video response under one minute, a resume or activity list, and two letters of recommendation. Selected candidates are invited to an Honors interview.' },
        ],
        note: 'Students who apply Regular Decision are not eligible for Honors admission, though they may still be considered for other merit scholarships.',
        source: 'honors',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'February 1' },
        rows: [
            { plan: 'Early Decision (Honors)', deadline: 'November 2, 2026', decision: 'December 15, 2026', binding: true },
            { plan: 'General admission', deadline: 'February 1, 2027', decision: 'Rolling, beginning December 16, 2026', binding: false },
        ],
        note: 'The deposit is due January 15, 2027 for Early Decision and May 3, 2027 for general admission. Applications received or completed after February 1 are waitlisted and considered as space allows. The application fee is $65, and an interview with an admissions officer is required. The priority FAFSA deadline is February 1.',
        source: 'deadlines',
        binding: 'Early Decision is binding: YU calls it its binding Early Decision program, and it is the only way into the Honors programs.',
        bindingSource: 'edNews',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'YU requires SAT or ACT scores only from Honors applicants. Everyone else may send scores but does not have to; scores you send must come directly from the testing service. YU does not require the writing section of either test.',
        source: 'freshman',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 49550 },
            { item: 'Mandatory undergraduate fees', amount: 4950 },
            { item: 'Housing (on campus, shared two-bed room)', amount: 11650 },
            { item: 'Food (basic meal plan)', amount: 4600 },
            { item: 'Books and supplies', amount: 1250 },
            { item: 'Transportation', amount: 1275 },
            { item: 'Personal expenses', amount: 5400 },
            { item: 'Health insurance (estimate)', amount: 6000 },
        ],
        total: 84675,
        note: 'YU\'s estimated budget for a full-time undergraduate for fall and spring, published by its international student office. YU says it runs about $10,000 above the YU bill because it counts personal costs the university does not charge.',
        source: 'costs',
        netPrice: {
            average: 49210,
            byIncome: [
                { income: '$0 to $30,000', amount: 31255 },
                { income: '$30,001 to $48,000', amount: 27653 },
                { income: '$48,001 to $75,000', amount: 32813 },
                { income: '$75,001 to $110,000', amount: 41814 },
                { income: 'Over $110,000', amount: 72673 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from YU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
